import { useRead, ReadState } from "@/components/LoadingStates";
import { useRef, useState } from "react";
import { useLocation, useNavigate, useSearchParams } from "react-router-dom";
import SectionTabs, { settingsTabsForLocation } from "@/components/SectionTabs";
import { AlertsContent } from "@/pages/Alerts";
import { useAuth } from "@/context/AuthContext";
import api, { formatError, mutationFailure } from "@/lib/api";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle, AlertDialogTrigger,
} from "@/components/ui/alert-dialog";
import { toast } from "sonner";
import { LogOut, MonitorSmartphone, Monitor, Smartphone, Tablet, RefreshCw } from "lucide-react";

function relativeTime(iso) {
  if (!iso) return "unknown";
  const then = new Date(iso).getTime();
  if (Number.isNaN(then)) return "unknown";
  const diff = Math.max(0, Date.now() - then);
  const s = Math.floor(diff / 1000);
  if (s < 45) return "just now";
  const m = Math.floor(s / 60);
  if (m < 60) return `${m}m ago`;
  const h = Math.floor(m / 60);
  if (h < 24) return `${h}h ago`;
  const d = Math.floor(h / 24);
  if (d < 30) return `${d}d ago`;
  return new Date(iso).toLocaleDateString();
}

function DeviceIcon({ type }) {
  const cls = "h-5 w-5";
  if (type === "mobile") return <Smartphone className={cls} />;
  if (type === "tablet") return <Tablet className={cls} />;
  return <Monitor className={cls} />;
}

export default function Settings() {
  const { user } = useAuth();
  const [params] = useSearchParams();
  const location = useLocation();
  const requested = params.get("tab");
  const activeKey = requested === "account" || requested === "notifications" ? requested : "security";
  const tabs = settingsTabsForLocation(location);

  return (
    <div className="space-y-6" data-testid="settings-page">
      <SectionTabs tabs={tabs} activeKey={activeKey} ariaLabel="Settings sections" data-testid="section-tabs-settings" />
      <div>
        <h1 className="font-display text-2xl font-bold">Settings</h1>
        <p className="text-sm text-muted-foreground mt-1">Manage your account, security and notifications.</p>
      </div>
      {activeKey === "account" && (
        <Card className="max-w-2xl p-5" data-testid="settings-account-panel">
          <h2 className="font-semibold mb-4">Account</h2>
          <dl className="space-y-3">
            <div><dt className="text-sm text-muted-foreground">Name</dt><dd className="break-words">{user?.name}</dd></div>
            <div><dt className="text-sm text-muted-foreground">Email</dt><dd className="break-words">{user?.email}</dd></div>
          </dl>
        </Card>
      )}
      {activeKey === "security" && <SecurityPanel />}
      {activeKey === "notifications" && <div data-testid="settings-notifications-panel"><AlertsContent embedded /></div>}
    </div>
  );
}

function SecurityPanel() {
  const { user, logoutEverywhere, refresh } = useAuth();
  const [busy, setBusy] = useState(false);
  const sessionsRead = useRead("/auth/sessions", (d) => d && Array.isArray(d.sessions) && d.sessions.every((s) => s && typeof s.session_id === "string"));
  const sessions = sessionsRead.data?.sessions || [];
  const loadingSessions = sessionsRead.status === "loading";
  const setSessions = (fn) => sessionsRead.setData((previous) => ({ ...previous, sessions: fn(previous.sessions) }));
  const [revokingId, setRevokingId] = useState(null);
  const nav = useNavigate();

  const loadSessions = () => sessionsRead.reload().catch(() => {});

  const handleRevoke = async (sid) => {
    setRevokingId(sid);
    try {
      await api.post(`/auth/sessions/${sid}/revoke`);
      toast.success("That device has been signed out.");
      setSessions((prev) => prev.filter((s) => s.session_id !== sid));
    } catch (e) {
      toast.error(formatError(e));
    } finally {
      setRevokingId(null);
    }
  };

  const [logoutAllError, setLogoutAllError] = useState(null);
  const logoutAllPending = useRef(false);
  const handleLogoutAll = async () => {
    if (logoutAllPending.current) return;
    logoutAllPending.current = true;
    setBusy(true);
    setLogoutAllError(null);
    try {
      await logoutEverywhere();
      toast.success("Le serveur a révoqué toutes vos sessions.");
      nav("/login");
    } catch (e) {
      // Report only what is known: a confirmed refusal, or an unknown outcome that is re-read (never replayed).
      const kind = mutationFailure(e);
      let message;
      if (kind === "session") {
        message = "Votre session avait déjà expiré sur cet appareil. La révocation des autres sessions n'est pas confirmée : reconnectez-vous puis consultez la liste des sessions.";
      } else if (kind === "refused") {
        message = `Le serveur a refusé la déconnexion globale : ${formatError(e)}. Aucune session n'a été révoquée par cette demande.`;
      } else {
        message = `Résultat inconnu : la déconnexion globale a pu aboutir avant l'erreur (${formatError(e)}).`;
        loadSessions();
        try {
          await refresh();
          message += " Relecture : cette session est toujours active, la déconnexion globale n'a donc pas pris effet sur cet appareil. Consultez la liste des sessions avant de réessayer.";
        } catch (readError) {
          message += readError?.response?.status === 401
            ? " Relecture : cette session n'est plus valide sur cet appareil ; reconnectez-vous pour vérifier les autres sessions."
            : " La relecture a échoué : le résultat reste inconnu. Consultez la liste des sessions avant de réessayer.";
        }
      }
      setLogoutAllError(message);
      toast.error(message);
    } finally {
      logoutAllPending.current = false;
      setBusy(false);
    }
  };

  const otherCount = sessions.filter((s) => !s.current).length;

  return (
    <div className="max-w-2xl" data-testid="settings-security-panel">
      <p className="text-sm text-muted-foreground mb-8">
        Manage the security of your OutlierPulse account.
      </p>

      {/* Active sessions */}
      <Card className="p-5 mb-6" data-testid="active-sessions-card">
        <div className="flex items-start justify-between gap-3 mb-4">
          <div className="flex items-start gap-3">
            <div className="mt-0.5 flex h-9 w-9 items-center justify-center rounded-lg bg-primary/10 text-primary shrink-0">
              <MonitorSmartphone className="h-5 w-5" />
            </div>
            <div>
              <h2 className="text-base font-semibold">Where you're logged in</h2>
              <p className="text-sm text-muted-foreground mt-1">
                These are the devices and browsers currently signed in to your account. Review them
                before signing out everywhere.
              </p>
            </div>
          </div>
          <Button
            variant="ghost" size="sm" onClick={loadSessions} disabled={loadingSessions}
            aria-label="Refresh sessions" title="Refresh sessions"
            data-testid="sessions-refresh-button" className="shrink-0"
          >
            <RefreshCw className={`h-4 w-4 ${loadingSessions ? "animate-spin" : ""}`} />
          </Button>
        </div>

        <ReadState read={sessionsRead} scope="sessions" empty={!sessions.length} title="Aucune session active disponible">
          <ul className="divide-y divide-border rounded-lg border border-border" data-testid="sessions-list">
            {sessions.map((s) => (
              <li
                key={s.session_id}
                className="flex items-center gap-3 p-3"
                data-testid={`session-row-${s.session_id}`}
              >
                <div className="flex h-9 w-9 items-center justify-center rounded-lg bg-muted text-muted-foreground shrink-0">
                  <DeviceIcon type={s.device_type} />
                </div>
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 flex-wrap">
                    <span className="text-sm font-medium truncate">{s.label}</span>
                    {s.current && (
                      <span
                        className="text-[11px] font-semibold px-2 py-0.5 rounded-full bg-emerald-500/15 text-emerald-600 dark:text-emerald-400"
                        data-testid="session-current-badge"
                      >
                        This device
                      </span>
                    )}
                  </div>
                  <p className="text-xs text-muted-foreground mt-0.5">
                    {s.current ? "Active now" : `Last active ${relativeTime(s.last_seen_at)}`}
                  </p>
                </div>
                {!s.current && (
                  <Button
                    variant="outline" size="sm"
                    onClick={() => handleRevoke(s.session_id)}
                    disabled={revokingId === s.session_id}
                    data-testid={`session-revoke-${s.session_id}`}
                    className="shrink-0"
                  >
                    {revokingId === s.session_id ? "Signing out…" : "Log out"}
                    {/* Every row has the same visible label: name the device it acts on. */}
                    <span className="sr-only">{` ${s.label || "unnamed device"}, last active ${relativeTime(s.last_seen_at)}`}</span>
                  </Button>
                )}
              </li>
            ))}
          </ul>
        </ReadState>
      </Card>

      {/* Log out of all devices */}
      <Card className="p-5">
        <div className="flex items-start gap-3">
          <div className="mt-0.5 flex h-9 w-9 items-center justify-center rounded-lg bg-destructive/10 text-destructive shrink-0">
            <LogOut className="h-5 w-5" />
          </div>
          <div className="flex-1">
            <h2 className="text-base font-semibold">Log out of all devices</h2>
            <p className="text-sm text-muted-foreground mt-1">
              Signs you out everywhere — this device and any other browser or phone where you're
              logged in as <span className="font-medium text-foreground">{user?.email}</span>. Use this
              if you've lost a device or suspect someone else has access. You'll need to sign in again.
            </p>

            <AlertDialog>
              <AlertDialogTrigger asChild>
                <Button variant="destructive" className="mt-4" disabled={busy} data-testid="logout-all-button">
                  <LogOut className="h-4 w-4 mr-2" />
                  Log out of all devices
                </Button>
              </AlertDialogTrigger>
              <AlertDialogContent data-testid="logout-all-confirm-dialog">
                <AlertDialogHeader>
                  <AlertDialogTitle>Log out of all devices?</AlertDialogTitle>
                  <AlertDialogDescription>
                    This will immediately end every active session for your account
                    {otherCount > 0 ? ` (${otherCount} other ${otherCount === 1 ? "device" : "devices"} plus this one)` : ", including this one"}.
                    Any signed-in device will be asked to log in again. This can't be undone.
                  </AlertDialogDescription>
                </AlertDialogHeader>
                <AlertDialogFooter>
                  <AlertDialogCancel data-testid="logout-all-cancel">Cancel</AlertDialogCancel>
                  <AlertDialogAction
                    onClick={handleLogoutAll}
                    disabled={busy}
                    data-testid="logout-all-confirm"
                    className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
                  >
                    {busy ? "Signing out…" : "Yes, log out everywhere"}
                  </AlertDialogAction>
                </AlertDialogFooter>
              </AlertDialogContent>
            </AlertDialog>
            {logoutAllError && <p role="alert" className="mt-3 text-sm text-destructive" data-testid="logout-all-error">{logoutAllError}</p>}
          </div>
        </div>
      </Card>
    </div>
  );
}
